import { Button, Card, CardContent, Dialog, DialogActions, DialogContent, Grid } from '@mui/material'
import Autocomplete from 'components/forms/Autocomplete'
import NumberField from 'components/forms/NumberField'
import Select from 'components/forms/Select'
import { DefaultDialogTitle } from 'components/styled/Dialog'
import { StyledFieldSet, StyledLegend } from 'components/StyledElements'
import React, { useEffect, useMemo, useState } from 'react'
import { shallowEqual, useSelector } from 'react-redux'
import i18n from 'simple-react-i18n'
import useSandreList from 'utils/customHook/useSandreList'
import useUpdateEffect from 'utils/customHook/useUpdateEffect'
import PropTypes from 'prop-types'
import { compact, isNil, orderBy } from 'lodash'
import { SUPPORT_MACROPHYTES } from '../../../constants/HydrobioConstant'
import useAccountSetting from 'utils/customHook/useAccountSetting'

// le back attend des nombres pour l'unité et la remarque : les listes renvoient des codes en texte
const toNumber = value => (isNil(value) || value === '' ? undefined : Number(value))
const formatHydrobioList = hydrobioList => ({
    ...hydrobioList,
    unit: toNumber(hydrobioList.unit),
    remark: toNumber(hydrobioList.remark),
})

// seul le taxon est obligatoire fonctionnellement (le back n'exige que les identifiants, fournis par l'opération)
const isValidHydrobioList = hydrobioList => !isNil(hydrobioList.taxon) && hydrobioList.taxon !== ''

const FORM_STEP = 'FORM_STEP'
const POSITION_STEP = 'POSITION_STEP'

const HydrobioListPositionStep = ({
    previousListId,
    onChange = () => {},
}) => {
    const {
        hydrobioList,
        taxons,
    } = useSelector(store => ({
        hydrobioList: store.OperationReducer.hydrobioList,
        taxons: store.TaxonReducer.taxons,
    }), shallowEqual)

    const options = useMemo(() => orderBy(hydrobioList, 'id').map(({ id, taxon: taxonCode }) => ({
        id,
        label: `${taxons.find(t => t.code === taxonCode)?.latinName ?? ''} [${taxonCode ?? ''}]`,
    })), [hydrobioList, taxons])

    return (
        <Card>
            <CardContent>
                <Grid container>
                    <Grid size={6}>
                        <Select
                            label={i18n.insertAfterTaxon}
                            options={options}
                            value={previousListId}
                            keyValue='id'
                            keyLabel='label'
                            nullLabel={i18n.atTheEndOfTheList}
                            noSort
                            onChange={onChange}
                        />
                    </Grid>
                </Grid>
            </CardContent>
        </Card>
    )
}

HydrobioListPositionStep.propTypes = {
    previousListId: PropTypes.number,
    onChange: PropTypes.func,
}

const HydrobioListDialogActions = ({
    isCreation = false,
    step,
    setStep = () => {},
    isValid = false,
    onValidate = () => {},
}) => {
    if (isCreation && step === FORM_STEP) {
        return (
            <DialogActions>
                <Button onClick={() => setStep(POSITION_STEP)} variant='contained' color='primary' disabled={!isValid}>
                    {i18n.next}
                </Button>
            </DialogActions>
        )
    }
    return (
        <DialogActions>
            {isCreation && (
                <Button onClick={() => setStep(FORM_STEP)} variant='outlined'>
                    {i18n.previous}
                </Button>
            )}
            <Button onClick={onValidate} variant='contained' color='primary' disabled={!isValid}>
                {i18n.validate}
            </Button>
        </DialogActions>
    )
}

HydrobioListDialogActions.propTypes = {
    isCreation: PropTypes.bool,
    step: PropTypes.oneOf([FORM_STEP, POSITION_STEP]),
    setStep: PropTypes.func,
    isValid: PropTypes.bool,
    onValidate: PropTypes.func,
}

const HydrobioFaunalDialog = ({
    isOpen = false,
    onClose = () => {},
    onValidate = () => {},
    title = '',
    hydrobioList: hydrobioListProps = {},
    microSampling = false,
}) => {
    const {
        taxons,
        remarks,
        units,
        sandreCodes,
        supportThemes,
    } = useSelector(store => ({
        taxons: store.TaxonReducer.taxons,
        remarks: store.OperationReducer.remarks,
        units: store.UnitReducer.units,
        sandreCodes: store.ReferencialReducer.sandreCodes,
        supportThemes: store.SupportReducer.supportThemes,
    }), shallowEqual)

    const [hydrobioList, setHydrobioList] = useState(hydrobioListProps)
    const [step, setStep] = useState(FORM_STEP)
    const [previousListId, setPreviousListId] = useState(undefined)
    const isCreation = isNil(hydrobioListProps.taxon)

    const developLexicon = useSandreList('TAXONS.DEVELOPPEMENT')
    const sizeLexicon = useMemo(() => {
        const list1 = hydrobioList.taxon ? sandreCodes.filter(c => c.field === `TAXONS.${hydrobioList.taxon}.TAILLE`) : undefined
        return list1?.length ? list1 : sandreCodes.filter(c => c.field === 'TAXONS.TAILLE')
    }, [sandreCodes, hydrobioList.taxon])
    const trustLexicon = useSandreList('ANALYSES.CONFIANCE')

    const onChange = obj => setHydrobioList(prevHL => ({ ...prevHL, ...obj }))

    useUpdateEffect(() => {
        if (isOpen) {
            setHydrobioList(hydrobioListProps)
            setStep(FORM_STEP)
            setPreviousListId(undefined)
        }
    }, [isOpen])

    useEffect(() => {
        if (!microSampling) return
        const p1 = hydrobioList.enumerationP1 ?? 0
        const p2 = hydrobioList.enumerationP2 ?? 0
        const p3 = hydrobioList.enumerationP3 ?? 0
        const p4 = hydrobioList.enumerationP4 ?? 0
        onChange({ enumerationA: p1 + p2 + p3 + p4 })
    }, [hydrobioList.enumerationP1, hydrobioList.enumerationP2, hydrobioList.enumerationP3, hydrobioList.enumerationP4, microSampling])

    useEffect(() => {
        if (!microSampling) return
        const p5 = hydrobioList.enumerationP5 ?? 0
        const p6 = hydrobioList.enumerationP6 ?? 0
        const p7 = hydrobioList.enumerationP7 ?? 0
        const p8 = hydrobioList.enumerationP8 ?? 0
        onChange({ enumerationB: p5 + p6 + p7 + p8 })
    }, [hydrobioList.enumerationP5, hydrobioList.enumerationP6, hydrobioList.enumerationP7, hydrobioList.enumerationP8, microSampling])

    useEffect(() => {
        if (!microSampling) return
        const p9 = hydrobioList.enumerationP9 ?? 0
        const p10 = hydrobioList.enumerationP10 ?? 0
        const p11 = hydrobioList.enumerationP11 ?? 0
        const p12 = hydrobioList.enumerationP12 ?? 0
        onChange({ enumerationC: p9 + p10 + p11 + p12 })
    }, [hydrobioList.enumerationP9, hydrobioList.enumerationP10, hydrobioList.enumerationP11, hydrobioList.enumerationP12, microSampling])

    const showOmnidiaCode = useAccountSetting('showOmnidiaCode', d => d === '1')
    const showMacrophyteCode = useAccountSetting('viewMacrophyteCode', d => d === '1')
    const showMNH = useAccountSetting('viewMuseumNaturalHistoryCode', d => d === '1')
    const showAspe = useAccountSetting('viewAspeCode', d => d === '1')
    const showWorms = useAccountSetting('viewWormsCode', d => d === '1')

    const codeAccess = compact([
        ...(showOmnidiaCode ? ['omnidiaCode', 'omnidiaSynonym', 'teraOmnidia', 'spomnidia'] : []),
        showMacrophyteCode && 'macrophyteCode',
        showMNH && 'refmuseum',
        showAspe && 'aspe',
        showWorms && 'worms',
    ])

    const themes = supportThemes.filter(st => st.codeSupport === hydrobioList.support).map(st => st.codeTheme)

    const taxonsFormatted = useMemo(() => {
        const filteredList = themes.length ? taxons.filter(t => themes.includes(t.theme)) : taxons

        return filteredList.map(fl => {
            const codes = compact(codeAccess.map(ca => fl[ca]))
            return {
                ...fl,
                label: `${fl.latinName ?? ''} [${fl.code ?? ''}] ${codes.length ? `${codes.join(' - ')}` : ''}`,
            }
        })
    }, [taxons, themes])

    return (
        <Dialog
            maxWidth='lg'
            fullWidth
            open={isOpen}
        >
            <DefaultDialogTitle
                title={title}
                onClose={onClose}
            />
            <DialogContent style={{ overflowX: 'hidden' }}>
                {step === POSITION_STEP ? (
                    <HydrobioListPositionStep previousListId={previousListId} onChange={setPreviousListId} />
                ) : (
                    <Card>
                        <CardContent>
                            <Grid container rowSpacing={1} columnSpacing={2}>
                                <Grid size={6}>
                                    <Select
                                        label={i18n.taxon}
                                        obligatory
                                        options={taxonsFormatted}
                                        value={hydrobioList.taxon}
                                        onChange={taxon => onChange({ taxon })}
                                        disabled={!isNil(hydrobioListProps.taxon)}
                                    />
                                </Grid>
                                <Grid size={3}>
                                    <Autocomplete
                                        label={i18n.unit}
                                        options={units}
                                        value={hydrobioList.unit}
                                        onChange={unit => onChange({ unit })}
                                        keyLabel='symbolWithName'
                                        keyFilter={['name', 'symbol', 'code']}
                                    />
                                </Grid>
                                <Grid size={3}>
                                    <Select
                                        options={remarks}
                                        value={hydrobioList.remark}
                                        label={i18n.remark}
                                        keyLabel='name'
                                        keyValue='code'
                                        returnNull
                                        onChange={remark => onChange({ remark })}
                                    />
                                </Grid>
                                <Grid size={3}>
                                    <Select
                                        label={i18n.development}
                                        value={hydrobioList.develop}
                                        options={developLexicon}
                                        keyLabel='name'
                                        keyValue='code'
                                        returnNull
                                        onChange={v => onChange({ develop: v })}
                                    />
                                </Grid>
                                <Grid size={3}>
                                    <Select
                                        label={i18n.trust}
                                        value={hydrobioList.trust}
                                        options={trustLexicon}
                                        keyLabel='name'
                                        keyValue='code'
                                        returnNull
                                        onChange={v => onChange({ trust: v })}
                                    />
                                </Grid>
                                <Grid size={3}>
                                    <NumberField
                                        title={i18n.density}
                                        value={hydrobioList.density}
                                        floatValue
                                        onChange={v => onChange({ density: v })}
                                    />
                                </Grid>
                                <Grid size={3}>
                                    <Select
                                        label={i18n.size}
                                        value={hydrobioList.size}
                                        options={sizeLexicon}
                                        keyLabel='name'
                                        keyValue='code'
                                        returnNull
                                        onChange={v => onChange({ size: v })}
                                        noSort
                                    />
                                </Grid>
                                {/* <Grid size={3}>
                                <NumberField // calcule indice boolean
                                    title={i18n.indice}
                                    value={hydrobioList.index}
                                    floatValue
                                    // onChange={result => setAnalysis(prev => ({ ...prev, result }))}
                                />
                            </Grid> */}
                                <Grid size={12}>
                                    <StyledFieldSet>
                                        <StyledLegend>{i18n.phaseA}</StyledLegend>
                                        <Grid container rowSpacing={1} columnSpacing={2}>
                                            <Grid size={3}>
                                                <NumberField
                                                    title={`${i18n.enumeration} A`}
                                                    value={hydrobioList.enumerationA}
                                                    floatValue
                                                    onChange={v => onChange({ enumerationA: v })}
                                                    disabled={microSampling}
                                                />
                                            </Grid>
                                            <Grid size={3}>
                                                <NumberField
                                                    title={`${i18n.percent} A`}
                                                    value={hydrobioList.percentageA}
                                                    floatValue
                                                    onChange={v => onChange({ percentageA: v })}
                                                    disabled={microSampling}
                                                />
                                            </Grid>
                                            {microSampling && (
                                                <>
                                                    <Grid size={6} />
                                                    <Grid size={3}>
                                                        <NumberField
                                                            title={`${i18n.enumeration} P1`}
                                                            value={hydrobioList.enumerationP1}
                                                            floatValue
                                                            onChange={v => onChange({ enumerationP1: v })}
                                                        />
                                                    </Grid>
                                                    <Grid size={3}>
                                                        <NumberField
                                                            title={`${i18n.enumeration} P2`}
                                                            value={hydrobioList.enumerationP2}
                                                            floatValue
                                                            onChange={v => onChange({ enumerationP2: v })}
                                                        />
                                                    </Grid>
                                                    <Grid size={3}>
                                                        <NumberField
                                                            title={`${i18n.enumeration} P3`}
                                                            value={hydrobioList.enumerationP3}
                                                            floatValue
                                                            onChange={v => onChange({ enumerationP3: v })}
                                                        />
                                                    </Grid>
                                                    <Grid size={3}>
                                                        <NumberField
                                                            title={`${i18n.enumeration} P4`}
                                                            value={hydrobioList.enumerationP4}
                                                            floatValue
                                                            onChange={v => onChange({ enumerationP4: v })}
                                                        />
                                                    </Grid>
                                                </>
                                            )}
                                        </Grid>
                                    </StyledFieldSet>
                                </Grid>
                                <Grid size={12}>
                                    <StyledFieldSet>
                                        <StyledLegend>{i18n.phaseB}</StyledLegend>
                                        <Grid container rowSpacing={1} columnSpacing={2}>
                                            <Grid size={3}>
                                                <NumberField
                                                    title={`${i18n.enumeration} B`}
                                                    value={hydrobioList.enumerationB}
                                                    floatValue
                                                    onChange={v => onChange({ enumerationB: v })}
                                                    disabled={microSampling}
                                                />
                                            </Grid>
                                            <Grid size={3}>
                                                <NumberField
                                                    title={`${i18n.percent} B`}
                                                    value={hydrobioList.percentageB}
                                                    floatValue
                                                    onChange={v => onChange({ percentageB: v })}
                                                    disabled={microSampling}
                                                />
                                            </Grid>
                                            {microSampling && (
                                                <>
                                                    <Grid size={6} />
                                                    <Grid size={3}>
                                                        <NumberField
                                                            title={`${i18n.enumeration} P5`}
                                                            value={hydrobioList.enumerationP5}
                                                            floatValue
                                                            onChange={v => onChange({ enumerationP5: v })}
                                                        />
                                                    </Grid>
                                                    <Grid size={3}>
                                                        <NumberField
                                                            title={`${i18n.enumeration} P6`}
                                                            value={hydrobioList.enumerationP6}
                                                            floatValue
                                                            onChange={v => onChange({ enumerationP6: v })}
                                                        />
                                                    </Grid>
                                                    <Grid size={3}>
                                                        <NumberField
                                                            title={`${i18n.enumeration} P7`}
                                                            value={hydrobioList.enumerationP7}
                                                            floatValue
                                                            onChange={v => onChange({ enumerationP7: v })}
                                                        />
                                                    </Grid>
                                                    <Grid size={3}>
                                                        <NumberField
                                                            title={`${i18n.enumeration} P8`}
                                                            value={hydrobioList.enumerationP8}
                                                            floatValue
                                                            onChange={v => onChange({ enumerationP8: v })}
                                                        />
                                                    </Grid>
                                                </>
                                            )}
                                        </Grid>
                                    </StyledFieldSet>
                                </Grid>
                                <Grid size={12}>
                                    <StyledFieldSet>
                                        <StyledLegend>{i18n.phaseC}</StyledLegend>
                                        <Grid container rowSpacing={1} columnSpacing={2}>
                                            <Grid size={3}>
                                                <NumberField
                                                    title={`${i18n.enumeration} C`}
                                                    value={hydrobioList.enumerationC}
                                                    floatValue
                                                    onChange={v => onChange({ enumerationC: v })}
                                                    disabled={microSampling}
                                                />
                                            </Grid>
                                            <Grid size={3}>
                                                <NumberField
                                                    title={`${i18n.percent} C`}
                                                    value={hydrobioList.percentageC}
                                                    floatValue
                                                    onChange={v => onChange({ percentageC: v })}
                                                    disabled={microSampling}
                                                />
                                            </Grid>
                                            {microSampling && (
                                                <>
                                                    <Grid size={6} />
                                                    <Grid size={3}>
                                                        <NumberField
                                                            title={`${i18n.enumeration} P9`}
                                                            value={hydrobioList.enumerationP9}
                                                            floatValue
                                                            onChange={v => onChange({ enumerationP9: v })}
                                                        />
                                                    </Grid>
                                                    <Grid size={3}>
                                                        <NumberField
                                                            title={`${i18n.enumeration} P10`}
                                                            value={hydrobioList.enumerationP10}
                                                            floatValue
                                                            onChange={v => onChange({ enumerationP10: v })}
                                                        />
                                                    </Grid>
                                                    <Grid size={3}>
                                                        <NumberField
                                                            title={`${i18n.enumeration} P11`}
                                                            value={hydrobioList.enumerationP11}
                                                            floatValue
                                                            onChange={v => onChange({ enumerationP11: v })}
                                                        />
                                                    </Grid>
                                                    <Grid size={3}>
                                                        <NumberField
                                                            title={`${i18n.enumeration} P12`}
                                                            value={hydrobioList.enumerationP12}
                                                            floatValue
                                                            onChange={v => onChange({ enumerationP12: v })}
                                                        />
                                                    </Grid>
                                                </>
                                            )}
                                        </Grid>
                                    </StyledFieldSet>
                                </Grid>
                                <Grid size={12}>
                                    <StyledFieldSet>
                                        <StyledLegend>{i18n.phaseCbis}</StyledLegend>
                                        <Grid container rowSpacing={1} columnSpacing={2}>
                                            <Grid size={3}>
                                                <NumberField
                                                    title={`${i18n.enumeration} C'`}
                                                    value={hydrobioList.enumerationC2}
                                                    floatValue
                                                    onChange={v => onChange({ enumerationC2: v })}
                                                    disabled={microSampling}
                                                />
                                            </Grid>
                                        </Grid>
                                    </StyledFieldSet>
                                </Grid>
                            </Grid>
                        </CardContent>
                    </Card>
                )}
            </DialogContent>
            <HydrobioListDialogActions
                isCreation={isCreation}
                step={step}
                setStep={setStep}
                isValid={isValidHydrobioList(hydrobioList)}
                onValidate={() => onValidate({ ...formatHydrobioList(hydrobioList), previousListId })}
            />
        </Dialog>
    )
}

HydrobioFaunalDialog.propTypes = {
    isOpen: PropTypes.bool,
    onClose: PropTypes.func,
    onValidate: PropTypes.func,
    title: PropTypes.string,
    hydrobioList: PropTypes.shape({}),
    microSampling: PropTypes.bool,
}

const HydrobioFloristicDialog = ({
    isOpen = false,
    onClose = () => {},
    onValidate = () => {},
    title = '',
    hydrobioList: hydrobioListProps = {},
}) => {
    const {
        taxons,
        remarks,
        units,
        sandreCodes,
        hydrobioOperation,
        supportThemes,
    } = useSelector(store => ({
        taxons: store.TaxonReducer.taxons,
        remarks: store.OperationReducer.remarks,
        units: store.UnitReducer.units,
        sandreCodes: store.ReferencialReducer.sandreCodes,
        hydrobioOperation: store.OperationReducer.hydrobioOperation,
        supportThemes: store.SupportReducer.supportThemes,
    }), shallowEqual)

    const [hydrobioList, setHydrobioList] = useState(hydrobioListProps)
    const [step, setStep] = useState(FORM_STEP)
    const [previousListId, setPreviousListId] = useState(undefined)
    const isCreation = isNil(hydrobioListProps.taxon)

    const developLexicon = useSandreList('TAXONS.DEVELOPPEMENT')
    const trustLexicon = useSandreList('ANALYSES.CONFIANCE')
    const sizeLexicon = useMemo(() => {
        const list1 = hydrobioList.taxon ? sandreCodes.filter(c => c.field === `TAXONS.${hydrobioList.taxon}.TAILLE`) : undefined
        return list1?.length ? list1 : sandreCodes.filter(c => c.field === 'TAXONS.TAILLE')
    }, [sandreCodes, hydrobioList.taxon])

    const isUR = hydrobioOperation.support === SUPPORT_MACROPHYTES
    const themes = supportThemes.filter(st => st.codeSupport === hydrobioOperation.support).map(st => st.codeTheme)

    const onChange = obj => setHydrobioList(prevHL => ({ ...prevHL, ...obj }))

    useUpdateEffect(() => {
        if (isOpen) {
            setHydrobioList(hydrobioListProps)
            setStep(FORM_STEP)
            setPreviousListId(undefined)
        }
    }, [isOpen])

    const showOmnidiaCode = useAccountSetting('showOmnidiaCode', d => d === '1')
    const showMacrophyteCode = useAccountSetting('viewMacrophyteCode', d => d === '1')
    const showMNH = useAccountSetting('viewMuseumNaturalHistoryCode', d => d === '1')
    const showAspe = useAccountSetting('viewAspeCode', d => d === '1')
    const showWorms = useAccountSetting('viewWormsCode', d => d === '1')

    const codeAccess = compact([
        ...(showOmnidiaCode ? ['omnidiaCode', 'omnidiaSynonym', 'teraOmnidia', 'spomnidia'] : []),
        showMacrophyteCode && 'macrophyteCode',
        showMNH && 'refmuseum',
        showAspe && 'aspe',
        showWorms && 'worms',
    ])

    const taxonsFormatted = useMemo(() => {
        const filteredList = themes.length ? taxons.filter(t => themes.includes(t.theme)) : taxons

        return filteredList.map(fl => {
            const codes = compact(codeAccess.map(ca => fl[ca]))
            return {
                ...fl,
                label: `${fl.latinName ?? ''} [${fl.code ?? ''}] ${codes.length ? `- ${codes.join(' - ')}` : ''}`,
            }
        })
    }, [taxons, themes])

    return (
        <Dialog
            maxWidth='lg'
            fullWidth
            open={isOpen}
        >
            <DefaultDialogTitle
                title={title}
                onClose={onClose}
            />
            <DialogContent style={{ overflowX: 'hidden' }}>
                {step === POSITION_STEP ? (
                    <HydrobioListPositionStep previousListId={previousListId} onChange={setPreviousListId} />
                ) : (
                    <Card>
                        <CardContent>
                            <Grid container rowSpacing={1} columnSpacing={2}>
                                <Grid size={6}>
                                    <Select
                                        label={i18n.taxon}
                                        obligatory
                                        options={taxonsFormatted}
                                        value={hydrobioList.taxon}
                                        onChange={taxon => onChange({ taxon, size: undefined })}
                                        disabled={!isNil(hydrobioListProps.taxon)}
                                    />
                                </Grid>
                                { isUR ? (
                                    <>
                                        <Grid size={1.5}>
                                            <NumberField
                                                title='UR1'
                                                value={hydrobioList.percentageA}
                                                floatValue
                                                onChange={v => onChange({ percentageA: v })}
                                                min={0}
                                                max={100}
                                            />
                                        </Grid>
                                        <Grid size={1.5}>
                                            <NumberField
                                                title='UR2'
                                                value={hydrobioList.percentageB}
                                                floatValue
                                                onChange={v => onChange({ percentageB: v })}
                                                min={0}
                                                max={100}
                                            />
                                        </Grid>
                                    </>
                                ) : (
                                    <Grid size={3}>
                                        <NumberField
                                            title={i18n.enumeration}
                                            value={hydrobioList.enumerationA}
                                            floatValue
                                            onChange={v => onChange({ enumerationA: v })}
                                        />
                                    </Grid>
                                )}
                                <Grid size={3}>
                                    <Autocomplete
                                        label={i18n.unit}
                                        options={units}
                                        value={hydrobioList.unit}
                                        onChange={unit => onChange({ unit })}
                                        keyLabel='symbolWithName'
                                        keyFilter={['name', 'symbol', 'code']}
                                    />
                                </Grid>
                                <Grid size={3}>
                                    <Select
                                        options={remarks}
                                        value={hydrobioList.remark}
                                        label={i18n.remark}
                                        keyLabel='name'
                                        keyValue='code'
                                        returnNull
                                        onChange={remark => onChange({ remark })}
                                    />
                                </Grid>
                                <Grid size={3}>
                                    <Select
                                        label={i18n.development}
                                        value={hydrobioList.develop}
                                        options={developLexicon}
                                        keyLabel='name'
                                        keyValue='code'
                                        returnNull
                                        onChange={v => onChange({ develop: v })}
                                    />
                                </Grid>
                                <Grid size={3}>
                                    <Select
                                        label={i18n.trust}
                                        value={hydrobioList.trust}
                                        options={trustLexicon}
                                        keyLabel='name'
                                        keyValue='code'
                                        returnNull
                                        onChange={v => onChange({ trust: v })}
                                    />
                                </Grid>
                                <Grid size={3}>
                                    <NumberField
                                        title={i18n.density}
                                        value={hydrobioList.density}
                                        floatValue
                                        onChange={v => onChange({ density: v })}
                                    />
                                </Grid>
                                <Grid size={3}>
                                    <Select
                                        label={i18n.size}
                                        value={hydrobioList.size}
                                        options={sizeLexicon}
                                        keyLabel='name'
                                        keyValue='code'
                                        returnNull
                                        onChange={v => onChange({ size: v })}
                                        noSort
                                    />
                                </Grid>
                            </Grid>
                        </CardContent>
                    </Card>
                )}
            </DialogContent>
            <HydrobioListDialogActions
                isCreation={isCreation}
                step={step}
                setStep={setStep}
                isValid={isValidHydrobioList(hydrobioList)}
                onValidate={() => onValidate({ ...formatHydrobioList(hydrobioList), previousListId })}
            />
        </Dialog>
    )
}

HydrobioFloristicDialog.propTypes = {
    isOpen: PropTypes.bool,
    onClose: PropTypes.func,
    onValidate: PropTypes.func,
    title: PropTypes.string,
    hydrobioList: PropTypes.shape({}),
}

export {
    HydrobioFaunalDialog,
    HydrobioFloristicDialog,
}